// The coach's narrative, split into readable paragraphs.
//
// One splitter for every surface that shows the narrative. The ledger digest
// and /review each had their own rendering, and only /review honoured the
// model's paragraph breaks — so the same text read as four paragraphs on one
// page and one wall of prose on the other.
//
// Three passes, in order:
//   1. Honour explicit blank-line breaks. Current narratives use them (three
//      or four paragraphs each), so this is the path that normally runs.
//   2. Otherwise promote ALL-CAPS topic markers ("KEY HORIZON CHANGE:") to
//      paragraph breaks — older calls signposted transitions that way.
//   3. Split anything still over ~320 characters on sentence boundaries, three
//      sentences a chunk, so stored narratives from before the model wrote
//      paragraphs still read as paragraphs.

export function splitNarrative(text: string): string[] {
  const trimmed = text.trim();
  if (!trimmed) return [];

  // (1) Honour explicit paragraph breaks.
  const explicit = trimmed.split(/\n{2,}/).map((p) => p.trim()).filter(Boolean);
  if (explicit.length > 1) return explicit;

  // (2) Force breaks on ALL-CAPS topic markers ("KEY HORIZON CHANGE:",
  // "DELOAD:", etc.). Match 2+ uppercase words followed by a colon.
  const markered = trimmed.replace(/(\s)([A-Z][A-Z0-9 ]{2,}[A-Z]:)/g, '\n\n$2');
  const afterMarker = markered.split(/\n{2,}/).map((p) => p.trim()).filter(Boolean);

  // (3) For each chunk still too long, sentence-split and group in 3s.
  const out: string[] = [];
  for (const chunk of afterMarker) {
    if (chunk.length <= 320) {
      out.push(chunk);
      continue;
    }
    // Split on sentence boundaries: period/?/! followed by space + capital.
    // Hyphen-and-em-dash safe (won't split mid-clause).
    const sentences = chunk.split(/(?<=[.!?])\s+(?=[A-Z0-9])/);
    let buf: string[] = [];
    for (const s of sentences) {
      buf.push(s);
      if (buf.length >= 3) {
        out.push(buf.join(' '));
        buf = [];
      }
    }
    if (buf.length > 0) out.push(buf.join(' '));
  }
  return out;
}
